"use client";

import "swiper/css";

import { useRef, useState } from "react";

import Image from "next/image";

import type { RefObject } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { ProductDetailDrawerContentNav } from "@/components/productDetailDrawer/productDetailDrawerContentNav.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import type { GetAccessoryDetailPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

export const ProductDetailsDrawerAdtAppCarouselSection = ({
  data,
}: ProductDetailsDrawerAdtAppCarouselSectionProps) => {
  const items = data?.items ?? [];
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const currentItem = items[activeIndex];

  if (!items.length || !currentItem || !data?.title) return null;

  const multi = items.length > 1;

  const renderSlides = () => {
    return items.map(
      (
        item: NonNullable<
          NonNullable<
            NonNullable<GetAccessoryDetailPageQueryResult>["adtPlusApp"]
          >["items"]
        >[number],
      ) => {
        return (
          <SwiperSlide key={item._key}>
            {item.image ? (
              <div className="relative aspect-[4/3] w-full bg-black/20">
                <Image
                  src={urlFor(item.image)?.width(1400)?.url()}
                  alt={item.title}
                  fill
                  className="object-cover object-center"
                  sizes="(max-width: 900px) 100vw, 900px"
                />
              </div>
            ) : (
              <div className="aspect-[4/3] w-full bg-black/20" />
            )}
          </SwiperSlide>
        );
      },
    );
  };

  return (
    <section
      className="bg-secondary flex w-full flex-col text-white"
      aria-label="ADT+ app">
      <div className="mx-4 px-10 pt-10 pb-6 text-center sm:px-30">
        <Text variant="h5" component="h5" className="text-white">
          {data?.title}
        </Text>
      </div>

      <div className="w-full">
        <Swiper
          slidesPerView={1}
          loop={multi}
          allowTouchMove={multi}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}>
          {renderSlides()}
        </Swiper>
      </div>

      <div className="flex flex-col items-center gap-4 px-6 pt-8 pb-10 text-center sm:px-10">
        <Text variant="h5" component="p" className="text-primary">
          {currentItem.title}
        </Text>
        <Text variant="body2" component="p" className="max-w-lg text-white/95">
          <RichText>{currentItem.description}</RichText>
        </Text>

        <ProductDetailDrawerContentNav
          slideIndex={activeIndex}
          slides={items}
          swiperRef={swiperRef as RefObject<SwiperType>}
        />
      </div>
    </section>
  );
};

type ProductDetailsDrawerAdtAppCarouselSectionProps = {
  data: NonNullable<
    NonNullable<GetAccessoryDetailPageQueryResult>["adtPlusApp"]
  >;
};
